import React from 'react';

const isDev = process.env.NODE_ENV !== 'production';
const NO_PROVIDER = '_NP_';
function createUseContext(context) {
  return () => {
    const value = React.useContext(context);
    if (isDev && value === NO_PROVIDER) {
      // eslint-disable-next-line no-console
      console.warn('[constate] Component not wrapped within a Provider.');
    }
    return value;
  };
}
function constate(useValue, ...splitValues) {
  const Context = React.createContext(NO_PROVIDER);
  const Provider = props => {
    const value = useValue(props);
    const [createMemoDeps] = splitValues;
    const deps = createMemoDeps && createMemoDeps(value);

    // deps won't change between renders
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const memoizedValue = Array.isArray(deps) ? React.useMemo(() => value, [value]) : value;
    // eslint-disable-next-line react/destructuring-assignment
    return React.createElement(Context.Provider, { value: memoizedValue }, props.children);
  };

  if (isDev && useValue.name) {
    Context.displayName = `${useValue.name}.Context`;
    Provider.displayName = `${useValue.name}.Provider`;
  }
  // const useCounterContext = constate(...)
  const useContext = createUseContext(Context);
  // const { Context, Provider } = constate(...)
  useContext.Context = Context;
  useContext.Provider = Provider;
  const tuple = [];
  if (!splitValues.length) {
    // const [Provider, useCounterContext] = constate(...);
    tuple.push(Provider, createUseContext(Context));
  } else {
    const contexts = [];
    const SplitProvider = props => {
      const value = useValue(props);
      let children = props.children;
      for (let i = 0; i < contexts.length; i += 1) {
        const context = contexts[i];
        // splitValue may be a hook, but it won't change between re-renders
        const splitValue = splitValues[i];
        children = React.createElement(context.Provider, { value: splitValue(value) }, children);
      }
      return children;
    };
    if (isDev && useValue.name) {
      SplitProvider.displayName = `${useValue.name}.Provider`;
    }
    // const [Provider, useCount, useIncrement] = constate(...);
    tuple.push(SplitProvider);
    for (let i = 0; i < splitValues.length; i += 1) {
      const context = React.createContext(NO_PROVIDER);
      contexts.push(context);
      tuple.push(createUseContext(context));
    }
  }
  for (let i = 0; i < tuple.length; i += 1) {
    useContext[i] = tuple[i];
  }
  if (typeof Symbol === 'function' && Symbol.iterator) {
    useContext[Symbol.iterator] = /* istanbul ignore next */ () => tuple[Symbol.iterator]();
  }
  return useContext;
}
export default constate;
